import React, { useState } from 'react';
import { GoogleGenAI } from "@google/genai";
import { Search, Loader2, ExternalLink, Sparkles } from "lucide-react";
import Markdown from "react-markdown";
import { toast } from "sonner";

interface Props {
  projectId?: string;
  projectName: string;
  clientName: string;
  userUid?: string | null;
}

const extractMarkdownSection = (markdown: string, heading: string) => {
  const escapedHeading = heading.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = markdown.match(new RegExp(`^##\\s+${escapedHeading}\\s*\\n([\\s\\S]*?)(?=\\n##\\s+|$)`, "im"));
  return match?.[1]?.trim() || "";
};

const normalizeLogline = (value: string) =>
  value
    .replace(/^[*-]\s+/gm, "")
    .replace(/\n+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const extractGeneratedLogline = (markdown: string) => {
  const explicitLogline = normalizeLogline(extractMarkdownSection(markdown, "Logline"));
  if (explicitLogline) return explicitLogline;

  const overview = normalizeLogline(extractMarkdownSection(markdown, "Overview"));
  if (!overview) return "";

  const sentences = overview.match(/[^.!?]+[.!?]+/g)?.map((sentence) => sentence.trim()).filter(Boolean) || [];
  const fallback = sentences.slice(0, 2).join(" ").trim();
  return fallback || overview;
};

export function ProjectInsights({ projectId, projectName, clientName, userUid }: Props) {
  const [insights, setInsights] = useState<string | null>(null);
  const [links, setLinks] = useState<{ uri: string; title: string }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = async () => {
    if (!projectName.trim() || projectName === "Untitled Project") {
      setError("Please enter a valid Project Name first.");
      return;
    }

    if (!process.env.GEMINI_API_KEY) {
      setError("Gemini API Key is missing.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const ai = new GoogleGenAI({ apiKey: process.env.GEMINI_API_KEY });
      
      const prompt = `Search the internet (including IMDB, Screen Daily, Televisual, Broadcast, Televisual, Deadline, Variety, and official production/distributor sources) for information about the film or TV series '${projectName}'${clientName ? ` produced by '${clientName}'` : ''}. Return a concise Markdown briefing with these headings when information is available:

## Logline
One sharp sentence suitable for a premium project dashboard hero.

## Overview
A short 2-3 sentence summary of what the project is.

## Plot
One short paragraph on the premise or storyline.

## Cast & Crew
Bullet points for key cast, director, writer, producer, showrunner, and other notable creative leads.

## Locations
Bullet points for filming locations, production bases, studio locations, or setting if relevant.

## Release / Status
Bullet points covering broadcaster/streamer, release status, commissioning status, season information, or delivery timing.

## Bid-Relevant Notes
Bullet points with anything useful for a VFX bid such as genre, scale, period setting, creatures, action, environments, stunts, heavy CG potential, or likely shot complexity.

## Sources
Bullet points naming the most useful sources you relied on.

Keep it concise, well-structured, and factual. If a section has no reliable information, omit that section entirely.`;

      const response = await ai.models.generateContent({
        model: "gemini-3.1-flash-lite-preview",
        contents: prompt,
        config: {
          tools: [{ googleSearch: {} }],
        },
      });

      const responseText = response.text || "No insights found.";
      setInsights(responseText);

      const generatedLogline = extractGeneratedLogline(responseText);
      if (generatedLogline && projectId && userUid) {
        const saveRes = await fetch(`/api/projects/${projectId}/show-dashboard`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": userUid,
          },
          body: JSON.stringify({ logline: generatedLogline }),
        });

        const saveData = await saveRes.json().catch(() => ({}));
        if (!saveRes.ok) {
          throw new Error(saveData.error || "Generated intelligence, but failed to save the dashboard logline.");
        }

        toast.success("Dashboard logline updated from gathered intelligence.");
      }
      
      // Extract URLs
      const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks;
      if (chunks) {
        const extractedLinks = chunks
          .map((chunk: any) => chunk.web)
          .filter((web: any) => web && web.uri && web.title);
        setLinks(extractedLinks);
      } else {
        setLinks([]);
      }
    } catch (err) {
      console.error("Error fetching insights:", err);
      setError("Failed to fetch project insights. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
      <div className="p-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between shrink-0">
        <div className="flex flex-col">
          <h2 className="text-[10pt] font-semibold text-slate-900 dark:text-slate-100">Project Intelligence</h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">AI-powered insights and web intelligence for your project</p>
        </div>
        <button
          onClick={fetchInsights}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 rounded-lg text-xs font-medium transition-colors disabled:opacity-50"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {loading ? "Scouring the web..." : "Gather Intelligence"}
        </button>
      </div>

      <div className="p-4">
        {error && (
          <div className="p-4 bg-red-50 text-red-700 rounded-lg text-[10pt]">
            {error}
          </div>
        )}

        {!insights && !loading && !error && (
          <div className="text-center py-8 text-slate-500 text-[10pt] border-2 border-dashed border-slate-200 rounded-lg">
            Click "Gather Intelligence" to search the web for cast, crew, and production details.
          </div>
        )}

        {insights && (
          <div className="space-y-6">
            <div className="prose prose-indigo max-w-none text-[10pt] prose-p:text-[10pt] prose-li:text-[10pt] prose-strong:text-[10pt] prose-headings:text-[10pt] prose-a:text-[10pt]">
              <div className="markdown-body">
                <Markdown>{insights}</Markdown>
              </div>
            </div>
            
            {links.length > 0 && (
              <div className="pt-4 border-t border-slate-100">
                <h4 className="text-[10px] font-medium text-slate-500 mb-2 uppercase tracking-wider">Sources</h4>
                <ul className="space-y-2">
                  {links.map((link, idx) => (
                    <li key={idx}>
                      <a 
                        href={link.uri} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[10pt] text-indigo-600 hover:text-indigo-800 flex items-center gap-1.5"
                      >
                        <ExternalLink className="w-3 h-3" />
                        {link.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
